import { useState, useEffect } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { Truck, X, AlertTriangle, Plus, Package, Clock, MapPinOff, Check, Undo2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import api from '../services/api';
import { API } from '../App';
import { toast } from 'sonner';

const ORANGE = '#ee7203';

const shortenName = (name) => {
  if (!name) return 'Unknown';
  return name.length > 18 ? `${name.slice(0, 16)}…` : name;
};

/** Pure presentational pill — reused by the draggable list item AND the DragOverlay preview. */
export const UnplannedPillView = ({ item, onDismiss, dragging, selectable, selected }) => (
  <div
    data-testid={onDismiss ? `unplanned-pill-${item.order_number || item.id}` : undefined}
    title={selectable ? 'Click to select for Move' : (item.reason ? `${item.reason}` : 'Drag onto a vehicle to plan')}
    className={`group flex-shrink-0 flex items-center gap-1.5 pl-2.5 pr-1.5 py-1.5 rounded-full border-2 select-none transition-all ${dragging ? 'shadow-2xl scale-105 cursor-grabbing' : selectable ? 'cursor-pointer' : 'cursor-grab hover:shadow-md'} ${selected ? 'ring-2 ring-offset-1 ring-green-500' : ''}`}
    style={{ borderColor: item.is_out_of_area ? '#b91c1c' : item.is_collection ? '#92400e' : item.is_today ? '#0f766e' : item.is_next_day ? '#1d4ed8' : item.reason ? '#f59e0b' : ORANGE, backgroundColor: item.is_out_of_area ? '#fef2f2' : item.is_collection ? '#fef3e2' : item.is_today ? '#f0fdfa' : item.is_next_day ? '#eff6ff' : item.reason ? '#fffbeb' : '#fff7ed' }}
  >
    {selectable && (
      <span className={`w-3.5 h-3.5 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${selected ? 'bg-green-500 border-green-500' : 'border-gray-400 bg-white'}`}>
        {selected && <Check className="w-2.5 h-2.5 text-white" />}
      </span>
    )}
    {item.is_out_of_area && (
      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full text-white whitespace-nowrap flex items-center gap-0.5 flex-shrink-0" style={{ backgroundColor: '#b91c1c' }} data-testid={`unplanned-out-of-area-badge-${item.id}`}>
        <MapPinOff className="w-2.5 h-2.5" /> OUT OF AREA
      </span>
    )}
    {item.is_collection && (
      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full text-white whitespace-nowrap flex items-center gap-0.5 flex-shrink-0" style={{ backgroundColor: '#92400e' }} data-testid={`unplanned-collection-badge-${item.id}`}>
        <Package className="w-2.5 h-2.5" /> COLLECTION
      </span>
    )}
    {item.is_today && (
      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full text-white whitespace-nowrap flex items-center gap-0.5 flex-shrink-0" style={{ backgroundColor: '#0f766e' }} data-testid={`unplanned-today-badge-${item.id}`}>
        <Clock className="w-2.5 h-2.5" /> TODAY
      </span>
    )}
    {item.is_next_day && (
      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full text-white whitespace-nowrap flex items-center gap-0.5 flex-shrink-0" style={{ backgroundColor: '#1d4ed8' }} data-testid={`unplanned-next-day-badge-${item.id}`}>
        <Clock className="w-2.5 h-2.5" /> NEXT DAY
      </span>
    )}
    {item.reason && <AlertTriangle className="w-3 h-3 text-amber-500 flex-shrink-0" />}
    {item.requires_moffet && <Truck className="w-3 h-3 flex-shrink-0" style={{ color: '#7c3aed' }} />}
    <span className="text-xs font-bold text-gray-800 whitespace-nowrap">{shortenName(item.customer_name)}</span>
    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-white/70 whitespace-nowrap" style={{ color: ORANGE }}>
      {item.postcode || 'No postcode'}
    </span>
    {item.order_number && (
      <span className="text-[10px] text-gray-500 whitespace-nowrap">#{item.order_number}</span>
    )}
    {onDismiss && (
      <button
        data-testid={`unplanned-pill-dismiss-${item.id}`}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => { e.stopPropagation(); onDismiss(item.id); }}
        className="opacity-0 group-hover:opacity-100 transition-opacity ml-0.5 flex-shrink-0 h-4 w-4 rounded-full bg-gray-200 hover:bg-red-200 flex items-center justify-center"
      >
        <X className="w-2.5 h-2.5 text-gray-600" />
      </button>
    )}
  </div>
);

const UnplannedPill = ({ item, onDismiss, selectMode, isSelected, onToggleSelect }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `unplanned-${item.id}`,
    data: { item },
    disabled: selectMode,
  });

  if (selectMode) {
    return (
      <div onClick={() => onToggleSelect(item)} data-testid={`unplanned-pill-select-${item.id}`}>
        <UnplannedPillView item={item} selectable selected={isSelected} />
      </div>
    );
  }

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={isDragging ? 'opacity-30' : ''}
    >
      <UnplannedPillView item={item} onDismiss={onDismiss} />
    </div>
  );
};

const UnplannedQueueBar = ({ date, refreshKey, branch, variant = 'classic', selectMode = false, selectedIds = [], onToggleSelect = () => {} }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [manualForm, setManualForm] = useState({ customer_name: '', postcode: '', order_number: '', weight: '' });
  const [duplicateOrderInfo, setDuplicateOrderInfo] = useState(null);
  const [canUndo, setCanUndo] = useState(false);
  const [undoing, setUndoing] = useState(false);
  // Toggle between the everyday Unplanned queue and Collection orders — both live in the
  // same `items` list from the API (is_collection flag), but showing them together made the
  // bar noisy, so only one set is visible at a time, switched via these two pill buttons.
  const [activeView, setActiveView] = useState('unplanned');
  const unplannedItems = items.filter((i) => !i.is_collection);
  const collectionItems = items.filter((i) => i.is_collection);
  const visibleItems = activeView === 'collection' ? collectionItems : unplannedItems;

  const load = () => {
    if (!date) return;
    setLoading(true);
    api.get(`${API}/transport-planning/unplanned/${date}?view_as_branch=${branch || ''}`)
      .then(res => setItems(res.data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date, refreshKey, branch]);

  const dismiss = async (id) => {
    const item = items.find(i => i.id === id);
    const label = item ? `${item.customer_name || 'this order'}${item.order_number ? ` (#${item.order_number})` : ''}` : 'this order';
    if (!window.confirm(`Remove ${label} from the Unplanned Queue? You can Undo this right after if it was a mistake.`)) {
      return;
    }
    try {
      await api.delete(`${API}/transport-planning/unplanned/${id}`);
      setItems(prev => prev.filter(i => i.id !== id));
      setCanUndo(true);
      toast.info('Removed from queue');
    } catch {
      toast.error('Failed to remove');
    }
  };

  const undoLastDelete = async () => {
    setUndoing(true);
    try {
      await api.post(`${API}/transport-planning/unplanned/undo?date=${date}&view_as_branch=${branch || ''}`);
      setCanUndo(false);
      load();
      toast.success('Restored to Unplanned Queue');
    } catch (e) {
      if (e?.response?.status === 404) toast.info('Nothing to undo');
      else toast.error('Failed to undo');
      setCanUndo(false);
    } finally {
      setUndoing(false);
    }
  };

  const addManual = async (skipDuplicateCheck = false) => {
    if (!manualForm.customer_name.trim() || !manualForm.postcode.trim()) {
      toast.error('Customer name and postcode are required');
      return;
    }
    const orderNum = manualForm.order_number.trim();
    if (!skipDuplicateCheck && orderNum) {
      try {
        const dupRes = await api.get(`${API}/transport-planning/check-duplicate/${encodeURIComponent(orderNum)}`);
        if (dupRes.data?.exists) {
          setDuplicateOrderInfo({ ...dupRes.data, order_number: orderNum });
          return;
        }
      } catch { /* fail open — don't block adding if the check itself fails */ }
    }
    try {
      await api.post(`${API}/transport-planning/unplanned`, {
        date, branch_id: branch || 'C30',
        customer_name: manualForm.customer_name.trim(),
        postcode: manualForm.postcode.trim().toUpperCase(),
        order_number: orderNum,
        weight: parseFloat(manualForm.weight) || 0,
      });
      setManualForm({ customer_name: '', postcode: '', order_number: '', weight: '' });
      setShowAddForm(false);
      load();
      toast.success('Added to Unplanned Queue');
    } catch {
      toast.error('Failed to add');
    }
  };

  const confirmMoveDuplicateHere = async () => {
    if (!duplicateOrderInfo) return;
    try {
      await api.post(`${API}/transport-planning/resolve-duplicate/${encodeURIComponent(duplicateOrderInfo.order_number)}`);
      setDuplicateOrderInfo(null);
      await addManual(true);
    } catch {
      toast.error('Failed to move the order — please try again');
    }
  };

  return (
    <>
    <div
      data-testid="unplanned-queue-bar"
      className={variant === 'glass' ? 'flex items-start gap-2' : 'z-30 border-2 border-dashed rounded-xl mb-3 px-3 py-2 flex items-start gap-2 bg-gray-50'}
      style={variant === 'glass' ? {} : { borderColor: '#d1d5db' }}
    >
      <div className="flex flex-col items-start gap-1 flex-shrink-0 pr-2 border-r border-gray-300 self-start pt-1">
        <button
          type="button"
          data-testid="unplanned-view-toggle-unplanned"
          onClick={() => setActiveView('unplanned')}
          className={`flex items-center gap-1.5 whitespace-nowrap transition-all rounded-full ${activeView === 'unplanned' ? '' : 'opacity-50 hover:opacity-80'}`}
        >
          <Truck className="w-4 h-4 text-gray-400" />
          <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">
            Unplanned{unplannedItems.length > 0 ? ` (${unplannedItems.length})` : ''}
          </span>
        </button>
        <button
          type="button"
          data-testid="unplanned-view-toggle-collection"
          onClick={() => setActiveView('collection')}
          className={`text-[11px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full border-2 whitespace-nowrap transition-all flex items-center gap-1 ${activeView === 'collection' ? 'text-white shadow-sm' : 'hover:shadow-sm'}`}
          style={activeView === 'collection' ? { backgroundColor: '#92400e', borderColor: '#92400e' } : { backgroundColor: 'transparent', borderColor: '#92400e', color: '#92400e' }}
        >
          <Package className="w-3 h-3" />
          Collection{collectionItems.length > 0 ? ` (${collectionItems.length})` : ''}
        </button>
      </div>

      <div className="flex items-start flex-wrap gap-1.5 flex-1 py-0.5">
        {loading ? (
          <span className="text-xs text-gray-400 py-1">Loading…</span>
        ) : visibleItems.length === 0 ? (
          <span className="text-xs text-gray-400 py-1" data-testid="unplanned-empty-state">
            {activeView === 'collection' ? 'No collection orders for this day' : 'No unplanned orders for this day'}
          </span>
        ) : (
          visibleItems.map(item => (
            <UnplannedPill
              key={item.id}
              item={item}
              onDismiss={dismiss}
              selectMode={selectMode}
              isSelected={selectedIds.includes(item.id)}
              onToggleSelect={onToggleSelect}
            />
          ))
        )}
      </div>

      {showAddForm ? (
        <div className="flex items-center gap-1.5 flex-shrink-0" data-testid="unplanned-manual-add-form">
          <input data-testid="unplanned-manual-customer" placeholder="Customer" value={manualForm.customer_name}
            onChange={(e) => setManualForm({ ...manualForm, customer_name: e.target.value })}
            className="h-7 text-xs px-2 border rounded w-28" />
          <input data-testid="unplanned-manual-postcode" placeholder="Postcode" value={manualForm.postcode}
            onChange={(e) => setManualForm({ ...manualForm, postcode: e.target.value })}
            className="h-7 text-xs px-2 border rounded w-20" />
          <input data-testid="unplanned-manual-order-number" placeholder="Order #" value={manualForm.order_number}
            onChange={(e) => setManualForm({ ...manualForm, order_number: e.target.value })}
            className="h-7 text-xs px-2 border rounded w-20" />
          <button data-testid="unplanned-manual-save-btn" onClick={() => addManual()} className="h-7 px-2 text-xs font-bold rounded text-white" style={{ backgroundColor: ORANGE }}>Save</button>
          <button onClick={() => setShowAddForm(false)} className="h-7 px-2 text-xs rounded bg-gray-200">Cancel</button>
        </div>
      ) : (
        <div className="flex-shrink-0 flex items-center gap-1.5">
          <button
            data-testid="unplanned-undo-btn"
            onClick={undoLastDelete}
            disabled={!canUndo || undoing}
            className={`h-7 w-7 rounded-full flex items-center justify-center border-2 transition-all ${canUndo ? 'hover:shadow-md' : 'opacity-30 cursor-not-allowed'}`}
            style={{ borderColor: canUndo ? '#2563eb' : '#9ca3af', color: canUndo ? '#2563eb' : '#9ca3af' }}
            title={canUndo ? 'Undo the last removed order' : 'Nothing to undo'}
          >
            <Undo2 className="w-4 h-4" />
          </button>
          <button
            data-testid="unplanned-manual-add-btn"
            onClick={() => setShowAddForm(true)}
            className="h-7 w-7 rounded-full flex items-center justify-center border-2 hover:shadow-md transition-all"
            style={{ borderColor: ORANGE, color: ORANGE }}
            title="Manually add an order to the queue"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>

    <Dialog open={!!duplicateOrderInfo} onOpenChange={(open) => { if (!open) setDuplicateOrderInfo(null); }}>
      <DialogContent className="max-w-md bg-white" data-testid="duplicate-order-modal-unplanned">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-700">
            <AlertTriangle className="w-5 h-5" />
            Order Already on Transport
          </DialogTitle>
        </DialogHeader>
        {duplicateOrderInfo && (
          <div className="space-y-3 pt-1">
            <p className="text-sm text-gray-700">
              Order <span className="font-mono font-bold">#{duplicateOrderInfo.order_number}</span> for{' '}
              <span className="font-bold">{duplicateOrderInfo.customer_name || 'this customer'}</span> is already{' '}
              {duplicateOrderInfo.location === 'board' ? (
                <>planned on <span className="font-bold">{duplicateOrderInfo.date}</span> ({duplicateOrderInfo.run_label}) on vehicle{' '}
                  <span className="font-bold">{duplicateOrderInfo.vehicle_name}</span></>
              ) : (
                <>sitting in the Unplanned Queue for <span className="font-bold">{duplicateOrderInfo.date}</span></>
              )}.
            </p>
            <p className="text-sm text-gray-600">
              Move it here instead? This will remove it from where it currently is, then add it to this day's queue.
            </p>
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => setDuplicateOrderInfo(null)} data-testid="duplicate-order-cancel-btn-unplanned">
                Cancel — Leave It There
              </Button>
              <Button onClick={confirmMoveDuplicateHere} style={{ backgroundColor: ORANGE, color: 'white' }} data-testid="duplicate-order-move-btn-unplanned">
                Move Here
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
    </>
  );
};

export default UnplannedQueueBar;
